import React, { Component } from "react";
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import { Grid, Typography } from "@material-ui/core";
import { Bar, Line } from 'react-chartjs-2';
import Spacer from '../../components/spacer/Spacer';
import StatCard from '../../components/stat-card/StatCard';
import OrdersList from '../orders-list/OrdersList';
import { TooltipConfig } from '../../utils/chart-config';
import { MonthNames } from "../../utils/date-utils";

import './DashboardData.scss';

export default class DashboardData extends Component {
    render() {
        const firstOrder = this.props.data[0].OrderDate;

        return (
            <div className="Dashboard-Data-wrapper">
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <CardContent>
                                <Typography variant="h4">
                                    You've spent a total of ${this.getTotalSpent()} since {firstOrder}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Spacer />

                    <StatCard
                        title={'Total orders'}
                        value={this.props.data.length} />

                    <StatCard
                        title={'Total spent'}
                        value={`$${this.getTotalSpent()}`} />

                    <StatCard
                        title={'First order'}
                        value={firstOrder} />

                    <Grid item xs={12}><div className="mb-4"></div></Grid>

                    <StatCard
                        size={'small'}
                        title={'Smallest purchase'}
                        value={`$${this.getSmallestPurchase()}`} />

                    <StatCard
                        title={'Largest purchase'}
                        value={`$${this.getBiggestPurchase()}`} />

                    <StatCard
                        title={'Avg purchase price'}
                        value={`$${this.getAveragePurchase()}`} />

                    <Spacer />

                    <Grid item xs={12}>
                        <YearlySpending data={this.props.data} />
                    </Grid>

                    <Spacer />

                    <Grid item xs={12}>
                        <MonthlySpending data={this.props.data} />
                    </Grid>

                    <Spacer />

                    <Grid item xs={12}>
                        <OrdersList orders={this.props.data} />
                    </Grid>
                </Grid>
            </div>
        )
    }

    getSum = (total, num) => {
        const chargeAmount = num.ItemTotal.length ? num.ItemTotal : 0;
        return total + Number(chargeAmount);
    };

    getTotalSpent() {
        return this.props.data.reduce(this.getSum, 0).toFixed(2);
    }

    getSmallestPurchase() {
        const expenses = this.props.data.map(order => order.ItemTotal);
        return Math.min.apply(Math, expenses.filter(value => value >= 1));
    }

    getBiggestPurchase() {
        return Math.max.apply(Math, this.props.data.map(order => order.ItemTotal));
    }

    getAveragePurchase() {
        return (this.getTotalSpent() / this.props.data.length).toFixed(2);
    }
}

class YearlySpending extends Component {
    constructor(props) {
        super(props);

        this.getDates = this.getDates.bind(this);
        this.getYears = this.getYears.bind(this);
    }

    render() {
        const years = this.getYears();
        const chartData = this.mapOrdersToYearlySpending(years);

        const data = {
            labels: [...new Set(years)],
            datasets: [
                {
                    label: 'Order History ($)',
                    backgroundColor: 'rgba(103, 58, 183, 1)',
                    borderColor: 'rgba(103, 58, 183, 1)',
                    borderWidth: 1,
                    hoverBackgroundColor: 'rgba(103, 58, 183, 1)',
                    hoverBorderColor: 'rgba(103, 58, 183, 1)',
                    data: chartData
                }
            ]
        };

        return (
            <React.Fragment>
                <Typography className="mb-3" variant="h4">Yearly spending</Typography>
                <Card>
                    <CardContent>
                        <Bar
                            data={data}
                            width={100}
                            height={300}
                            options={{
                                maintainAspectRatio: false,
                                tooltips: TooltipConfig
                            }}
                        />
                    </CardContent>
                </Card>
            </React.Fragment>
        )
    }

    getDates(data) {
        let datesArray = data.map(entry => entry.OrderDate);
        datesArray.sort((a, b) => new Date(a) - new Date(b));
        datesArray = [...new Set(datesArray)];

        return datesArray;
    }

    getYears() {
        return [...new Set(this.getDates(this.props.data).map(date => new Date(date).getFullYear()))];
    }

    mapOrdersToYearlySpending(years) {
        const spending = [];
        years.forEach(year => {
            spending.push(
                this.props.data.filter(order => new Date(order.OrderDate).getFullYear() === year)
                    .reduce((acc, curr) => {
                        let amount = curr.ItemTotal.replace('$', '');
                        amount = amount || 0;
                        return acc + parseInt(amount)
                    }, 0)
            );
        });

        return spending;
    }
}

class MonthlySpending extends Component {
    constructor(props) {
        super(props);

        this.mapOrdersToMonthlySpending = this.mapOrdersToMonthlySpending.bind(this);
    }

    render() {
        const chartData = this.mapOrdersToMonthlySpending();
        const monthlyValues = [];

        for (let month in chartData) {
            if (chartData.hasOwnProperty(month)) {
                monthlyValues.push(
                    chartData[month].reduce((acc, curr) => {
                        return acc + parseFloat(curr);
                    }, 0).toFixed(2)
                )
            }
        }

        const data = {
            labels: Object.keys(chartData).map(month => month.split('_').join(' ')),
            datasets: [
                {
                    label: 'Order History ($)',
                    backgroundColor: 'rgba(103, 58, 183, 0.35)',
                    borderColor: 'rgba(103, 58, 183, 1)',
                    borderWidth: 1,
                    hoverBackgroundColor: 'rgba(103, 58, 183, 0.7)',
                    hoverBorderColor: 'rgba(103, 58, 183, 1)',
                    data: monthlyValues
                }
            ]
        };

        return (
            <React.Fragment>
                <Typography className="mb-3" variant="h4">Monthly spending</Typography>
                <Card>
                    <CardContent>
                        <Line
                            data={data}
                            width={100}
                            height={350}
                            options={{
                                maintainAspectRatio: false,
                                tooltips: TooltipConfig,
                                elements: {
                                    point: {
                                        hitRadius: 0,
                                        radius: 0
                                    },
                                    line: {
                                        tension: 0.1
                                    }
                                }
                            }}
                        />
                    </CardContent>
                </Card>
            </React.Fragment>
        )
    }

    mapOrdersToMonthlySpending() {
        const spending = {};

        this.props.data.forEach((order, i) => {
            const orderDate = this.formatDate(new Date(order.OrderDate));

            const previousOrder = this.props.data[i - 1];
            const previousOrderDate = previousOrder && this.formatDate(new Date(previousOrder.OrderDate));

            if (!spending[orderDate]) {
                spending[orderDate] = [];
            }

            if (previousOrder && orderDate === previousOrderDate) {
                spending[previousOrderDate].push(order.ItemTotal);
            } else {
                spending[orderDate].push(order.ItemTotal);
            }
        });

        return spending;
    }

    formatDate(dateObj) {
        return  MonthNames[dateObj.getMonth()] + '_' + dateObj.getFullYear();
    }
}
